import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { FALLBACK_ICON, ICONS, IconPrimitive } from './icon-set';

/**
 * Icono SVG inline.
 *
 * Uso: `<app-icon name="credit-card" />`, `<app-icon name="star" [size]="14" />`.
 *
 * Hereda `currentColor`, así que se tiñe solo con el color del texto que lo
 * rodea. Es decorativo por defecto (`aria-hidden`): el significado lo pone el
 * texto de al lado. Cuando un icono va SOLO dentro de un botón, se le pasa
 * `label` para que tenga nombre accesible.
 */
@Component({
  selector: 'app-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg
      xmlns="http://www.w3.org/2000/svg"
      [attr.width]="size()"
      [attr.height]="size()"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      [attr.stroke-width]="strokeWidth()"
      stroke-linecap="round"
      stroke-linejoin="round"
      [attr.aria-hidden]="label() ? null : 'true'"
      [attr.role]="label() ? 'img' : null"
      [attr.aria-label]="label() || null"
    >
      @for (s of shapes(); track $index) {
        @switch (s.kind) {
          @case ('path') {
            <path [attr.d]="s.d" [attr.fill]="s.filled ? 'currentColor' : 'none'" />
          }
          @case ('circle') {
            <circle [attr.cx]="s.cx" [attr.cy]="s.cy" [attr.r]="s.r" />
          }
          @case ('line') {
            <line [attr.x1]="s.x1" [attr.y1]="s.y1" [attr.x2]="s.x2" [attr.y2]="s.y2" />
          }
          @case ('rect') {
            <rect
              [attr.x]="s.x"
              [attr.y]="s.y"
              [attr.width]="s.width"
              [attr.height]="s.height"
              [attr.rx]="s.rx"
            />
          }
          @case ('polyline') {
            <polyline [attr.points]="s.points" />
          }
        }
      }
    </svg>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        line-height: 0;
      }
    `,
  ],
})
export class IconComponent {
  readonly name = input.required<string>();
  readonly size = input<number>(18);
  /** Nombre accesible; solo para iconos que van solos, sin texto al lado. */
  readonly label = input<string>('');

  /** El trazo se afina al crecer para que el peso óptico no se dispare. */
  readonly strokeWidth = computed(() => (this.size() >= 32 ? 1.5 : 1.75));

  readonly shapes = computed<IconPrimitive[]>(() => ICONS[this.name()] ?? ICONS[FALLBACK_ICON]);
}
